import { type Option } from "@appretio/ui-kit";

/** The dialog fields the generate request is validated from. */
export interface GenerateInputs {
  /** Selected model values. */
  models: string[];
  /** Selected KPI values. */
  kpis: string[];
  /** Training-range start week option. */
  trainWeekFrom: Option | undefined;
  /** Training-range end week option. */
  trainWeekTo: Option | undefined;
  /** Forecast-horizon end week option. */
  forecastWeekTo: Option | undefined;
}

/** Per-field validation messages; an empty object means the inputs are valid. */
export interface GenerateInputErrors {
  models?: string;
  kpis?: string;
  trainWeekFrom?: string;
  trainWeekTo?: string;
  forecastWeekTo?: string;
}

/**
 * Validates the load dialog's inputs ahead of a generate request. Pure so the
 * range rules are unit-testable without rendering the dialog
 * (`CreateCubeModal.validateSources` is the shape).
 */
export function validateGenerateInputs(
  inputs: GenerateInputs
): GenerateInputErrors {
  const errors: GenerateInputErrors = {};
  if (inputs.models.length === 0) {
    errors.models = "At least one model is required";
  }
  if (inputs.kpis.length === 0) errors.kpis = "At least one KPI is required";
  const { trainWeekFrom, trainWeekTo, forecastWeekTo } = inputs;
  if (!trainWeekFrom) errors.trainWeekFrom = "Training range from is required";
  if (!trainWeekTo) errors.trainWeekTo = "Training range to is required";
  if (!forecastWeekTo) {
    errors.forecastWeekTo = "Forecast horizon to is required";
  }
  if (
    trainWeekFrom &&
    trainWeekTo &&
    Number(trainWeekFrom.value) > Number(trainWeekTo.value)
  ) {
    errors.trainWeekTo = "Must be on or after training range from";
  }
  if (
    trainWeekTo &&
    forecastWeekTo &&
    Number(forecastWeekTo.value) <= Number(trainWeekTo.value)
  ) {
    errors.forecastWeekTo = "Must be after training range to";
  }
  return errors;
}

/** What the week pickers are built from; each part can be missing on its own. */
export interface WeekSource {
  /** `weekPeriodMapping` has arrived with the forecast metadata. */
  hasWeekCalendar: boolean;
  /** Fiscal years the loaded grid shows. */
  fiscalYears: string[];
  /** The grid payload's `editable_week_from`. */
  editableWeekFrom: number | null | undefined;
  /** The grid payload's `forecast_week_from`. */
  forecastWeekFrom: number | null | undefined;
}

/**
 * Names the missing input that leaves the week pickers empty or without
 * defaults, for the dialog to show before Generate; `undefined` when the
 * pickers are complete. The first two cases leave nothing to pick, the
 * third only nothing preselected.
 */
export function describeMissingWeekSource({
  hasWeekCalendar,
  fiscalYears,
  editableWeekFrom,
  forecastWeekFrom
}: WeekSource): string | undefined {
  const pickers = "the training range and forecast horizon";
  if (!hasWeekCalendar) {
    return `Forecast metadata has not loaded, so there are no weeks to offer for ${pickers}.`;
  }
  if (fiscalYears.length === 0) {
    return `The loaded grid has no fiscal years, so there are no weeks to offer for ${pickers}.`;
  }
  const missing = [
    editableWeekFrom ? null : "editable_week_from",
    forecastWeekFrom ? null : "forecast_week_from"
  ].filter((field) => field !== null);
  if (missing.length === 0) return undefined;
  return `The grid has no ${missing.join(" or ")}, so ${pickers} have no defaults. Pick them under Advanced options.`;
}
